import type { AppearanceRow, PageNew } from '@setplex/tria-api'
import * as fallbacks from './fallbacks'

// helper function for style
function style(size: 'S' | 'M' | 'L', orientation: 'HORIZONTAL' | 'VERTICAL') {
  return {
    posterSize: size,
    posterOrientation: orientation,
    background: {
      type: 'UNSET',
    },
  } as AppearanceRow['style']
}

// helper function for resource
function resource(
  resource: string,
  fallback?: boolean | (() => Promise<unknown>)
) {
  return {
    path: resource.split('?')[0],
    search: new URLSearchParams(resource.split('?')[1]),
    fallback: fallback || undefined,
  } as AppearanceRow['resource']
}

// helper function for row
function row(r: Partial<AppearanceRow>, i: number): AppearanceRow {
  r.id = 'default_' + String(i + 1)
  r.description = undefined
  return r as AppearanceRow
}

export const APPEARANCE_DEFAULT_HOME_ROWS = (
  fallback?: boolean
): PageNew<AppearanceRow> => {
  return {
    content: [
      // 1
      {
        title: 'Continue Watching',
        localizedTitleKey: 'continue_watching',
        contentType: 'MIXED' as const,
        resource: resource('/v3/continue-watching'),
        paged: true,
        refreshable: true,
        style: style('S', 'HORIZONTAL'),
      },

      // 2
      {
        title: 'Featured Movies',
        localizedTitleKey: 'movies_featured',
        contentType: 'VOD' as const,
        resource: resource(
          '/v3/carousels/featured/content?types=VOD',
          fallback && fallbacks.featuredMoviesFallbackFx
        ),
        paged: false,
        refreshable: false,
        style: style('M', 'VERTICAL'),
      },

      // 3
      {
        title: 'Movies You May Like',
        localizedTitleKey: 'movies_may_like',
        contentType: 'VOD' as const,
        resource: resource('/v3/vods?only-recommended=true'),
        paged: true,
        refreshable: true,
        style: style('S', 'VERTICAL'),
      },

      // 4
      {
        title: 'Events',
        localizedTitleKey: 'event_other',
        contentType: 'LIVE_EVENT' as const,
        resource: resource('/v3/live-events?status=LIVE_SOON'),
        paged: true,
        refreshable: true,
        style: style('S', 'HORIZONTAL'),
      },

      // 5
      {
        title: 'Featured Series',
        localizedTitleKey: 'series_featured',
        contentType: 'TV_SHOW' as const,
        resource: resource(
          '/v3/carousels/featured/content?types=TV_SHOW',
          fallback && fallbacks.featuredSeriesFallbackFx
        ),
        paged: false,
        refreshable: false,
        style: style('M', 'VERTICAL'),
      },

      // 6
      {
        title: 'Featured Live Channels',
        localizedTitleKey: 'channel_featured',
        contentType: 'TV_CHANNEL' as const,
        resource: resource(
          '/v3/carousels/featured/content?types=TV_CHANNEL',
          fallback && fallbacks.featuredLiveChannelsFallbackFx
        ),
        paged: false,
        refreshable: false,
        style: style('M', 'HORIZONTAL'),
      },

      // 7
      {
        title: 'Live Channels You May Like',
        localizedTitleKey: 'live_channels_may_like',
        contentType: 'TV_CHANNEL' as const,
        resource: resource('/v3/channels/tv?only-recommended=true'),
        paged: false,
        refreshable: true,
        style: style('S', 'HORIZONTAL'),
      },

      // 8
      {
        title: 'Partner Products',
        localizedTitleKey: 'partner_products',
        contentType: 'PARTNER_PRODUCT' as const,
        resource: resource('/v3/partner-product'),
        paged: true,
        refreshable: false,
        style: style('S', 'HORIZONTAL'),
      },

      // 9
      {
        title: 'Recently Updated',
        localizedTitleKey: 'recently_updated',
        contentType: 'TV_SHOW' as const,
        resource: resource('/v3/tvshows?sort-by=updatedTime&sort-order=desc'),
        paged: true,
        refreshable: false,
        style: style('S', 'VERTICAL'),
      },

      // 10
      {
        title: 'Recently Watched Live Channels',
        localizedTitleKey: 'live_channels_recently_watched',
        contentType: 'TV_CHANNEL' as const,
        resource: resource(
          '/v3/channels/recently-watched',
          fallback && fallbacks.recentlyWatchedLiveChannelsFallbackFx
        ),
        paged: true,
        refreshable: true,
        style: style('S', 'HORIZONTAL'),
      },

      // 11
      {
        title: 'Series Extras',
        localizedTitleKey: 'series_bundle',
        contentType: 'TV_SHOW_BUNDLE' as const,
        resource: resource('/v3/bundles/tv-shows?sortOrder=asc'),
        paged: true,
        refreshable: false,
        style: style('S', 'HORIZONTAL'),
      },

      // 12
      {
        title: 'Movies Extras',
        localizedTitleKey: 'movies_bundle',
        contentType: 'VOD_BUNDLE' as const,
        resource: resource('/v3/bundles/vods?sortOrder=asc'),
        paged: true,
        refreshable: false,
        style: style('S', 'HORIZONTAL'),
      },

      // 13
      {
        title: 'Live Channels Extras',
        localizedTitleKey: 'channel_bundle',
        contentType: 'TV_BUNDLE' as const,
        resource: resource('/v3/bundles/channels?sortOrder=asc'),
        paged: true,
        refreshable: false,
        style: style('S', 'HORIZONTAL'),
      },

      // 14
      {
        title: 'Favorite Live Channels',
        localizedTitleKey: 'channel_favorites',
        contentType: 'TV_CHANNEL' as const,
        resource: resource('/v3/channels?only-favorites=true&type=TV'),
        paged: true,
        refreshable: true,
        style: style('S', 'HORIZONTAL'),
      },

      // 15
      {
        title: 'Favorite Movies',
        localizedTitleKey: 'movies_favorite',
        contentType: 'VOD' as const,
        resource: resource('/v3/vods?only-favorites=true'),
        paged: true,
        refreshable: true,
        style: style('S', 'VERTICAL'),
      },

      // 16
      {
        title: 'Favorite Series',
        localizedTitleKey: 'series_favorite',
        contentType: 'TV_SHOW' as const,
        resource: resource('/v3/tvshows?only-favorites=true'),
        paged: true,
        refreshable: true,
        style: style('S', 'VERTICAL'),
      },

      // 17
      {
        title: 'Last added Movies',
        localizedTitleKey: 'movies_last_added',
        contentType: 'VOD' as const,
        resource: resource('/v3/vods?sort-by=addedTime&sort-order=desc'),
        paged: true,
        refreshable: false,
        style: style('S', 'VERTICAL'),
      },

      // 18
      {
        title: 'Last added Series',
        localizedTitleKey: 'series_last_added',
        contentType: 'TV_SHOW' as const,
        resource: resource('/v3/tvshows?sort-by=addedTime&sort-order=desc'),
        paged: true,
        refreshable: false,
        style: style('S', 'VERTICAL'),
      },
    ].map(row),
    number: 0,
    size: 18,
    first: true,
    last: true,
  } satisfies PageNew<AppearanceRow>
}
